import "../../kendo.icons.js";
import "../../kendo.dropdownbutton.js";
import "../../kendo.button.js";
import { Widget } from "../../core/base/widget";
import { domUtilsService } from "../../core/services/dom-utils.service";
import { htmlService } from "../../core/services/html.service";
import { DragToScrollHandler } from "../../core/models/dom-utils";
import { NS, STYLES, REFERENCES, ICONS, SCROLLING_DELTA, CLICK, DOT, RTL, EVENTS } from "../constants";
import type { IChatOptions, IMessage, IToolbarAction } from "../models";
import type { DataManager } from "./data-manager.collaborator.js";
import { MessageToolbar } from "./message-toolbar.collaborator.js";
import { FileMenu } from "./file-menu.collaborator.js";
import { IKendoJQueryStatic, IKendoJQuery } from "../../core";
import { MessageRenderer, ScrollManager, SuggestionRenderer } from "./chat-view";

declare const kendo: any;
declare const $: IKendoJQueryStatic;

export interface IChatViewContext {
    dataManager: DataManager;
}

export class ChatView extends Widget {
    static options = {
        name: "ChatView"
    };

    private context: IChatViewContext;
    private list!: IKendoJQuery;
    private _dragHandler: DragToScrollHandler | null = null;
    private _messageRenderer!: MessageRenderer;
    private _scrollManager!: ScrollManager;
    private _suggestionRenderer!: SuggestionRenderer;

    private _hideOnFocusMessageTimes(): void {
        this.element
            .find(`${DOT + STYLES.messageTime}[data-timestamp-visibility='onFocus']`)
            .addClass(STYLES.hidden);
    }

    private _toggleOnFocusMessageTime(messageElement: IKendoJQuery, visible: boolean): void {
        messageElement
            .find(`${DOT + STYLES.messageTime}[data-timestamp-visibility='onFocus']`)
            .toggleClass(STYLES.hidden, !visible);
    }

    constructor(
        element: IKendoJQuery,
        options: Partial<IChatOptions>,
        context: IChatViewContext
    ) {
        super(element, $.extend(true, {}, ChatView.options, options));

        this.context = context;
        this._initList();
        this._initHelpers();
        this._attachEvents();
    }

    events: string[] = [
        EVENTS.suggestedActionClick,
        EVENTS.executeAction,
        EVENTS.messageToolbarExecute,
        EVENTS.fileMenuExecute,
        EVENTS.downloadAllFiles,
        EVENTS.expandableToggle,
        EVENTS.resendMessage
    ];

    private _initList(): void {
        const messages = (this.options as IChatOptions).messages;

        this.element
            .addClass(STYLES.viewWrapper)
            .attr("role", "log")
            .attr("aria-label", messages.messageListLabel);

        this.list = $("<div>")
            .addClass(STYLES.messageListContent)
            .appendTo(this.element);
    }

    private _initHelpers(): void {
        const options = this.options as IChatOptions;

        this._messageRenderer = new MessageRenderer({
            list: this.list,
            options,
            dataManager: this.context.dataManager,
            dataItem: this.dataItem.bind(this),
            getReplyMessage: this.getReplyMessage.bind(this),
            initMessageToolbar: this._initMessageToolbar.bind(this),
            initFileMenus: this._initFileMenus.bind(this)
        });

        this._scrollManager = new ScrollManager({
            element: this.element,
            options,
            list: this.list
        });

        this._suggestionRenderer = new SuggestionRenderer({
            list: this.list,
            options,
            dataManager: this.context.dataManager
        });
    }

    private _attachEvents(): void {
        this.element
            .on(CLICK + NS, this._listClick.bind(this))
            .on(CLICK + NS, DOT + STYLES.message, this._messageClick.bind(this))
            .on(CLICK + NS, DOT + STYLES.suggestion, this._suggestionClick.bind(this))
            .on(CLICK + NS, `[${REFERENCES.attachmentActionButton}]`, this._attachmentActionClick.bind(this))
            .on(CLICK + NS, `[${REFERENCES.leftScrollButton}]`, this._leftScrollButtonClick.bind(this))
            .on(CLICK + NS, `[${REFERENCES.rightScrollButton}]`, this._rightScrollButtonClick.bind(this))
            .on(CLICK + NS, DOT + STYLES.downloadButton, this._downloadAllClick.bind(this))
            .on(CLICK + NS, `[${REFERENCES.retryButton}]`, this._retryButtonClick.bind(this));

        this._attachDragToScrollEvents();
    }

    private _attachDragToScrollEvents(): void {
        this._dragHandler = domUtilsService.createDragToScrollHandler(
            this.element,
            {
                namespace: NS + ".chatViewDrag",
                captureElement: this.element,
                delegateSelector: `${DOT}${STYLES.suggestionsScroll}, ${DOT}${STYLES.cardDeck}`
            }
        );
        this._dragHandler.attach();
    }

    private _getScrollableElementFromButton(button: IKendoJQuery): IKendoJQuery {
        const scrollWrap = button.closest(`${DOT}${STYLES.suggestionScrollWrap}, ${DOT}${STYLES.cardDeckScrollWrap}`);

        if (scrollWrap.hasClass(STYLES.cardDeckScrollWrap)) {
            return scrollWrap.children(DOT + STYLES.cardDeck) as IKendoJQuery;
        }

        return scrollWrap.find(DOT + STYLES.suggestionsScroll) as IKendoJQuery;
    }

    private _retryButtonClick(e: any): void {
        e.preventDefault();
        e.stopPropagation();
        const button = $(e.currentTarget);
        const uid = button.attr("data-uid");
        const messageElement = button.closest(DOT + STYLES.message) as IKendoJQuery;
        const message = this._getMessageFromElement(messageElement);

        this.trigger(EVENTS.resendMessage, { message, uid });
    }

    private _attachmentActionClick(e: any): void {
        e.preventDefault();
        e.stopPropagation();

        const button = $(e.currentTarget);
        const attachmentIndex = Number(button.attr("data-attachment-index"));
        const actionIndex = Number(button.attr("data-action-index"));
        const messageElement = button.closest(DOT + STYLES.message) as IKendoJQuery;
        const message = this._getMessageFromElement(messageElement);
        const action = message?.attachments?.[attachmentIndex]?.actions?.[actionIndex];

        if (!action) {
            return;
        }

        this.trigger(EVENTS.executeAction, { action, message });
    }

    private _getMessageFromElement(element: IKendoJQuery): IMessage | null {
        const uid = element.attr("data-uid");
        if (!uid) {
            return null;
        }

        return this.context.dataManager.getMessageByUid(uid);
    }

    renderMessage(message: IMessage): void {
        this._messageRenderer.renderMessage(message);
    }

    _initMessageToolbar(element: IKendoJQuery, perMessageActions?: IToolbarAction[]): void {
        const options = this.options as IChatOptions;
        const messageToolbarActions = perMessageActions ?? options.messageToolbarActions;

        if (!messageToolbarActions || !messageToolbarActions.length || !element.length) {
            element.remove();
            return;
        }

        const messageToolbar = new MessageToolbar(element, {
            items: messageToolbarActions,
            dir: options.dir,
            messages: options.messages,
            resizable: false
        });

        messageToolbar.bind("execute", (e: any) => this.trigger(EVENTS.messageToolbarExecute, e));
    }

    _initFileMenus(element: IKendoJQuery, enableFileActions?: boolean): void {
        const options = this.options as IChatOptions;
        const fileActions = options.fileActions;
        const fileMenuButtons = element.find(`[${REFERENCES.fileMenuButton}]`);

        if (enableFileActions === false || !fileActions?.length || !element.length) {
            fileMenuButtons.remove();
            return;
        }

        fileMenuButtons.each((_i: number, el: HTMLElement) => {
            const $el = $(el);
            if (!$el.data("kendoDropDownButton")) {
                const menu = new FileMenu($el, { items: fileActions });
                menu.bind("execute", (e: any) => this.trigger(EVENTS.fileMenuExecute, e));
            }
        });
    }

    dataItem(message: IKendoJQuery): IMessage | null {
        const uid = message && message.data("uid");
        if (uid) {
            return this.context.dataManager.getMessageByUid(uid) || message.data("messageData") || null;
        }
        return message?.data("messageData") || null;
    }

    fileDataItem(message: IMessage, file: IKendoJQuery): any {
        const uid = file && file.data("uid");
        if (uid) {
            return this.context.dataManager.getFileByUid(message, uid);
        }
        return null;
    }

    getReplyMessage(message: IMessage): IMessage | null {
        if (!message?.replyToId) {
            return null;
        }

        const replyMessage = this.context.dataManager.getMessageById(message.replyToId);
        if (replyMessage) {
            (replyMessage as any).isOwnMessage = String(replyMessage.authorId) === String((this.options as IChatOptions).authorId);
        }

        return replyMessage;
    }

    renderSuggestedActions(): void {
        this._suggestionRenderer.renderSuggestedActions();
    }

    clearMessages(): void {
        this.list.find(DOT + STYLES.messageGroup).remove();
        this.list.find(DOT + STYLES.timestamp).remove();
        this._suggestionRenderer.removeSuggestedActions();
    }

    removeMessage(uid: string): boolean {
        const messageElement = this.list.find(DOT + STYLES.message + `[data-uid="${htmlService.encode(uid)}"]`);

        if (!messageElement.length) {
            return false;
        }

        const messageGroup = messageElement.closest(DOT + STYLES.messageGroup);
        const messagesInGroup = messageGroup.find(DOT + STYLES.message);

        if (messagesInGroup.length === 1) {
            messageGroup.remove();
        } else {
            messageElement.remove();
        }

        return true;
    }

    scrollToBottom(): void {
        this._scrollManager.scrollToBottom();
    }

    isNearBottom(): boolean {
        return this._scrollManager.isNearBottom();
    }

    scrollToMessage(uid: string): boolean {
        return this._scrollManager.scrollToMessage(uid);
    }

    updateSeparator(): void {
        this._scrollManager.updateSeparator();
    }

    refreshDataManager(dataManager: DataManager): void {
        this.context.dataManager = dataManager;
    }

    showNoData(html: string): void {
        this.list.html(html);
        this.list.addClass(STYLES.messageListContentEmpty);
    }

    hideNoData(): void {
        this.list.empty();
        this.list.removeClass(STYLES.messageListContentEmpty);
    }

    hasMessages(): boolean {
        return this.list.children(DOT + STYLES.messageGroup).length > 0;
    }

    private _listClick(e: any): void {
        const targetElement = $(e.target);

        if (targetElement.hasClass(STYLES.message) || targetElement.parents(DOT + STYLES.message).length) {
            return;
        }

        this._clearSelection(targetElement);
    }

    private _suggestionClick(e: any): void {
        const suggestionElement = $(e.currentTarget);

        if (suggestionElement.length) {
            this.trigger(EVENTS.suggestedActionClick, { text: suggestionElement.text() });
        }
    }

    private _messageClick(e: any): void {
        const target = $(e.target);
        const expandIcon = target.closest(`[${REFERENCES.bubbleExpandableIndicator}]`);

        if (this._allowMessageClick(target)) {
            return;
        }

        this._clearSelection(target);

        if (expandIcon.length) {
            const bubble = expandIcon.closest(DOT + STYLES.bubble);
            const isExpanded = bubble.hasClass(STYLES.expanded);

            bubble.toggleClass(STYLES.expanded, !isExpanded);
            expandIcon.html(kendo.ui.icon({ icon: isExpanded ? ICONS.chevronDown : ICONS.chevronUp }));

            this.trigger(EVENTS.expandableToggle, { indicator: expandIcon, isExpanded: !isExpanded });
            return;
        }

        const messageElement = $(e.currentTarget);
        const bubble = messageElement.find(DOT + STYLES.bubble);
        bubble.addClass(STYLES.selected);
        this._toggleOnFocusMessageTime(messageElement, true);
    }

    private _allowMessageClick(target: IKendoJQuery): boolean {
        const disallowedSelectors = [
            `[${REFERENCES.attachmentActionButton}]`,
            `[${REFERENCES.fileMenuButton}]`,
            DOT + STYLES.messageToolbar,
            DOT + STYLES.typingIndicator,
            DOT + STYLES.downloadButton
        ].join(", ");

        return target.closest(disallowedSelectors).length > 0 ||
            target.children(disallowedSelectors).length > 0 ||
            target.is(disallowedSelectors);
    }

    private _clearSelection(target: IKendoJQuery): void {
        const selectedMessages = this.element.find(DOT + STYLES.selected);

        if (target.closest(`[${REFERENCES.bubbleExpandableIndicator}]`).length) {
            return;
        }

        this._hideOnFocusMessageTimes();

        selectedMessages.each((_index: number, element: HTMLElement) => {
            const bubble = $(element);
            bubble.removeClass(STYLES.selected);
        });
    }

    private _leftScrollButtonClick(e: any): void {
        e.preventDefault();
        const button = $(e.currentTarget);
        const scrollableElement = this._getScrollableElementFromButton(button as IKendoJQuery);
        const isRtl = (this.options as IChatOptions).dir === RTL;

        if (!scrollableElement.length) {
            return;
        }

        const position = domUtilsService.scrollByDelta(scrollableElement, isRtl ? SCROLLING_DELTA : -SCROLLING_DELTA);

        button.siblings(`[${REFERENCES.rightScrollButton}]`).removeClass(STYLES.disabled);

        if (position.atStart) {
            button.addClass(STYLES.disabled);
        }
    }

    private _rightScrollButtonClick(e: any): void {
        e.preventDefault();
        const button = $(e.currentTarget);
        const scrollableElement = this._getScrollableElementFromButton(button as IKendoJQuery);
        const isRtl = (this.options as IChatOptions).dir === RTL;

        if (!scrollableElement.length) {
            return;
        }

        const position = domUtilsService.scrollByDelta(scrollableElement, isRtl ? -SCROLLING_DELTA : SCROLLING_DELTA);

        button.siblings(`[${REFERENCES.leftScrollButton}]`).removeClass(STYLES.disabled);

        if (position.atEnd) {
            button.addClass(STYLES.disabled);
        }
    }

    private _downloadAllClick(e: any): void {
        const downloadButton = $(e.currentTarget);
        const message = downloadButton.closest(DOT + STYLES.message);

        if (message.length) {
            this.trigger(EVENTS.downloadAllFiles, { messageElement: message });
        }
    }

    destroy(): void {
        super.destroy();

        this._scrollManager.destroy();

        if (this._dragHandler) {
            this._dragHandler.destroy();
        }

        this.element.find("[data-role='button']").each(function() {
            const fileMenu = $(this).data("kendoChatFileMenu");
            if (fileMenu) {
                fileMenu.destroy();
            }
        });

        this.element.empty();
        this.element.off(NS);

        (this as any).list = null;
    }
}
